// Cross-page registry for the "only one auto-sync at a time, device-wide"
// rule and the floating auto-sync indicator (see ActiveAutoSyncPill) - both
// need to enumerate every armed auto-sync record independent of whether any
// page currently has a live useLocalAutoSync instance mounted for it, since
// db.js's IndexedDB store (not React state) is the one thing that outlives
// page navigation.
import { getAllFolderSyncStates, getFolderSyncState, deleteFolderSyncState } from './db';
import { nativeSyncService, nativeFolderSyncProvider, isNativeSyncEnvironment } from './nativeProvider';
import { webFileSystemAccessProvider } from './provider';
import { closeAutoSyncNotification } from './syncNotification';

// Same provider selection as useLocalAutoSync.js's own defaultAutoSyncProvider -
// duplicated rather than imported from there since that module is a React hook
// file and this one deliberately isn't (see this file's own header comment).
const liveProvider = isNativeSyncEnvironment() ? nativeFolderSyncProvider : webFileSystemAccessProvider;

// storageKey is either a plain eventId (an organiser's own EventPage session)
// or `${eventId}:${token}` (an upload-link visitor session) - see db.js's own
// header comment. Parses back out which kind a record is, and for an
// upload-link one, the token needed to route back to it (see
// ActiveAutoSyncPill).
function describeAutoSync(record) {
  const storageKey = record.storageKey;
  const colonIndex = storageKey.indexOf(':');
  const isUploadLink = colonIndex !== -1;
  return {
    storageKey,
    eventId: record.eventId,
    folderName: record.dirHandle?.name || 'folder',
    isUploadLink,
    token: isUploadLink ? storageKey.slice(colonIndex + 1) : null,
    updatedAt: record.updatedAt || 0,
    needsReconnect: Boolean(record.needsReconnect),
  };
}

export async function getAllArmedAutoSyncs() {
  const all = await getAllFolderSyncStates();
  return all.filter((r) => r.armed).map(describeAutoSync);
}

// Used by ActiveAutoSyncPill, which has no live useLocalAutoSync instance to
// ask - its own needsReconnect flag (see describeAutoSync) is only ever
// refreshed by a live poll or mount-check inside that hook, both of which
// only run while an EventPage/UploadLinkPage tab hosting the armed auto-sync
// is actually open. If permission gets revoked (or a native SAF grant is
// lost) while the user is only ever looking at Landing/Dashboard - exactly
// where this pill lives - that flag stays stuck at its last-known `false`
// forever, and the pill would otherwise keep claiming "running" indefinitely.
// This re-derives the truth on demand by querying the persisted dirHandle
// directly, instead of trusting that possibly-stale flag.
export async function getActiveAutoSyncForPill() {
  const all = await getAllArmedAutoSyncs();
  const mostRecent = all.filter((a) => !a.needsReconnect).sort((a, b) => b.updatedAt - a.updatedAt)[0];
  if (!mostRecent) return null;
  const rec = await getFolderSyncState(mostRecent.eventId, mostRecent.storageKey).catch(() => null);
  if (!rec?.dirHandle) return mostRecent;
  try {
    const perm = await liveProvider.queryPermission(rec.dirHandle);
    if (perm !== 'granted') return null;
  } catch {
    return null;
  }
  return mostRecent;
}

// Used before arming a NEW auto-sync (see useLocalAutoSync.js's selectFolder) -
// only one auto-sync may run at a time device-wide. Background folder
// watching + uploading is real, continuous work (a foreground service/
// WorkManager job on native, a live poll loop in-browser); running two
// independent watchers at once - even for two different events - was a real
// product ask to prevent, not just a resource nicety. Returns the other one's
// description, or null if none is armed besides (optionally) this same one.
export async function findOtherArmedAutoSync(excludeStorageKey) {
  const others = await getAllArmedAutoSyncs();
  return others.find((o) => o.storageKey !== String(excludeStorageKey)) || null;
}

// Stops an auto-sync this page didn't itself arm - there's no live
// useLocalAutoSync hook instance for it to call its own stopSync() on, since
// that other auto-sync's page (EventPage or UploadLinkPage) isn't the one
// currently mounted. Mirrors stopSync()'s own native+IndexedDB cleanup
// exactly, minus the react-state reset only a live instance would also need.
// Best-effort: if that other auto-sync happens to have a live hook instance
// open in a different browser tab right now, that tab's own in-memory state
// won't learn about this until its next reload/interaction - there's no
// cross-tab signalling for this feature, matching other known gaps in this
// module (see e.g. useLocalAutoSync.js's activeSubmissions doc comment).
export async function stopAutoSyncRemote(eventId, storageKey) {
  await nativeSyncService.stopForegroundSync(storageKey);
  await deleteFolderSyncState(eventId, storageKey);
  closeAutoSyncNotification(storageKey);
}

// Stops every armed auto-sync on this device outright, regardless of which
// event/link it belongs to - used when the signed-in identity itself changes
// (see AuthContext.js's login/logout) since an armed folder's uploads are
// bound to whoever was signed in (or anonymous) at arm time; carrying it
// across a login/logout would otherwise keep uploading under the now-stale
// identity. In practice at most one record is ever armed at a time (enforced
// at arm-time by findOtherArmedAutoSync), but this doesn't assume that.
export async function stopAllArmedAutoSyncs() {
  const armed = await getAllArmedAutoSyncs();
  await Promise.all(armed.map((r) => stopAutoSyncRemote(r.eventId, r.storageKey)));
}

// Looks up an armed auto-sync by the organiser's own EventPage storageKey
// convention (plain eventId, see db.js's header comment) - used to offer a
// "Stop watching" action on EventPage's own "event not found/no access" error
// screen (event deleted from elsewhere on this device, or access revoked)
// where useLocalAutoSync's own hook never mounts (the page returns its error
// branch before reaching the section that hosts it), which would otherwise
// leave an armed folder with no reachable in-app control to turn it off.
export async function findArmedAutoSyncForEvent(eventId) {
  if (!eventId) return null;
  const rec = await getFolderSyncState(eventId);
  return rec && rec.armed ? describeAutoSync(rec) : null;
}

// Same as findArmedAutoSyncForEvent, but for a guest's UploadLinkPage: unlike
// the organiser flow, the eventId isn't known independently of the very API
// call that's failing (getUploadLinkPageData) when the link has been revoked
// or its event deleted, so this instead searches every armed record for the
// one whose storageKey encodes this exact upload-link token (see db.js's
// header comment - `${eventId}:${token}`) regardless of eventId.
export async function findArmedAutoSyncForToken(token) {
  if (!token) return null;
  const all = await getAllArmedAutoSyncs();
  return all.find((r) => r.isUploadLink && r.token === token) || null;
}
